import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Bar, Doughnut } from 'react-chartjs-2';
import type { ChartOptions, TooltipItem } from 'chart.js';

import { fetchAccountingSummary, type AccountingPeriodSummary } from '../../services/accounting';
import { listRestaurants, type RestaurantRecord } from '../../services/restaurants';
import { ensureChartsRegistered } from '../analytics/chartSetup';

ensureChartsRegistered();

const currency = (v: number) => `₺${v.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}`;

function getDefaultDateRange(): { from: string; to: string } {
  const now = new Date();
  const from = new Date(now.getFullYear(), now.getMonth(), 1);
  return {
    from: from.toISOString().slice(0, 10),
    to: now.toISOString().slice(0, 10),
  };
}

const EXPENSE_COLORS = [
  '#ef4444', '#f97316', '#eab308', '#22c55e', '#14b8a6',
  '#3b82f6', '#8b5cf6', '#ec4899', '#64748b', '#6366f1',
];

const INCOME_COLORS = [
  '#2563eb', '#9333ea', '#16a34a', '#f97316', '#06b6d4', '#ec4899',
];

const PAYMENT_COLORS = ['#22c55e', '#3b82f6', '#f97316'];

export default function AccountingSummary() {
  const defaults = getDefaultDateRange();
  const [restaurant, setRestaurant] = useState('');
  const [dateFrom, setDateFrom] = useState(defaults.from);
  const [dateTo, setDateTo] = useState(defaults.to);

  const { data: restaurants = [] } = useQuery<RestaurantRecord[]>({
    queryKey: ['restaurants'],
    queryFn: listRestaurants,
  });

  const {
    data: summary,
    isLoading,
    error,
  } = useQuery<AccountingPeriodSummary>({
    queryKey: ['accounting', 'summary', restaurant, dateFrom, dateTo],
    queryFn: () =>
      fetchAccountingSummary(
        restaurant || undefined,
        dateFrom || undefined,
        dateTo || undefined,
      ),
  });

  // Daily bar chart
  const dailyChartData = useMemo(() => {
    if (!summary || summary.dailyBreakdown.length === 0) return null;
    const labels = summary.dailyBreakdown.map((d) =>
      new Date(d.date).toLocaleDateString('tr-TR', {
        day: '2-digit',
        month: 'short',
        timeZone: 'Europe/Istanbul',
      }),
    );
    return {
      labels,
      datasets: [
        {
          label: 'Gelir',
          data: summary.dailyBreakdown.map((d) => d.income),
          backgroundColor: 'rgba(34, 197, 94, 0.7)',
          borderRadius: 4,
        },
        {
          label: 'Gider',
          data: summary.dailyBreakdown.map((d) => d.expenses),
          backgroundColor: 'rgba(239, 68, 68, 0.7)',
          borderRadius: 4,
        },
      ],
    };
  }, [summary]);

  const barOptions: ChartOptions<'bar'> = {
    responsive: true,
    plugins: {
      legend: { position: 'top' },
      tooltip: {
        callbacks: {
          label: (ctx: TooltipItem<'bar'>) => `${ctx.dataset.label}: ₺${(ctx.parsed.y ?? 0).toFixed(2)}`,
        },
      },
    },
    scales: {
      y: {
        ticks: {
          callback: (v) => `₺${typeof v === 'number' ? v.toFixed(0) : '0'}`,
        },
      },
    },
  };

  // Expense by category doughnut
  const expenseDoughnut = useMemo(() => {
    if (!summary || summary.expensesByCategory.length === 0) return null;
    return {
      labels: summary.expensesByCategory.map((c) => c.category),
      datasets: [{
        data: summary.expensesByCategory.map((c) => c.total),
        backgroundColor: summary.expensesByCategory.map((_, i) => EXPENSE_COLORS[i % EXPENSE_COLORS.length]),
      }],
    };
  }, [summary]);

  // Income by source doughnut
  const incomeDoughnut = useMemo(() => {
    if (!summary || summary.incomeBySource.length === 0) return null;
    return {
      labels: summary.incomeBySource.map((s) => s.source),
      datasets: [{
        data: summary.incomeBySource.map((s) => s.total),
        backgroundColor: summary.incomeBySource.map((_, i) => INCOME_COLORS[i % INCOME_COLORS.length]),
      }],
    };
  }, [summary]);

  // Income by payment method doughnut
  const paymentDoughnut = useMemo(() => {
    if (!summary || summary.incomeByPaymentMethod.length === 0) return null;
    return {
      labels: summary.incomeByPaymentMethod.map((m) => m.method),
      datasets: [{
        data: summary.incomeByPaymentMethod.map((m) => m.total),
        backgroundColor: summary.incomeByPaymentMethod.map((_, i) => PAYMENT_COLORS[i % PAYMENT_COLORS.length]),
      }],
    };
  }, [summary]);

  const doughnutOptions: ChartOptions<'doughnut'> = {
    responsive: true,
    plugins: {
      legend: { position: 'bottom' },
      tooltip: {
        callbacks: {
          label: (ctx: TooltipItem<'doughnut'>) => {
            const value = ctx.parsed ?? 0;
            return `${ctx.label}: ₺${value.toFixed(2)}`;
          },
        },
      },
    },
  };

  return (
    <>
      {/* Filters */}
      <div className="card compact">
        <p className="card-title">Dönem Seç</p>
        <div className="form-grid three-columns">
          <div className="form-field">
            <label>Restoran</label>
            <select value={restaurant} onChange={(e) => setRestaurant(e.target.value)}>
              <option value="">Tüm Restoranlar</option>
              {restaurants.map((r) => (
                <option key={r.id} value={r.id}>{r.name}</option>
              ))}
            </select>
          </div>
          <div className="form-field">
            <label>Başlangıç</label>
            <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
          </div>
          <div className="form-field">
            <label>Bitiş</label>
            <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
          </div>
        </div>
      </div>

      {isLoading ? (
        <div className="empty-state"><p>Özet yükleniyor...</p></div>
      ) : error ? (
        <div className="empty-state">
          <strong>Özet yüklenemedi</strong>
          <p>{error instanceof Error ? error.message : 'Bilinmeyen hata'}</p>
        </div>
      ) : summary ? (
        <>
          {/* KPI cards */}
          <div className="insight-grid">
            <div className="insight-card">
              <span>Toplam Gelir</span>
              <strong className="trend-up">{currency(summary.totalIncome)}</strong>
            </div>
            <div className="insight-card">
              <span>Toplam Gider</span>
              <strong className="trend-down">{currency(summary.totalExpenses)}</strong>
            </div>
            <div className="insight-card">
              <span>Ödenen KDV</span>
              <strong>{currency(summary.totalKdv)}</strong>
            </div>
            <div className="insight-card">
              <span>Net Kâr / Zarar</span>
              <strong className={summary.netProfit >= 0 ? 'trend-up' : 'trend-down'}>
                {currency(summary.netProfit)}
              </strong>
            </div>
          </div>

          {/* Daily chart */}
          {dailyChartData && (
            <div className="card">
              <p className="card-title">Günlük Gelir vs Gider</p>
              <Bar data={dailyChartData} options={barOptions} />
            </div>
          )}

          {/* Doughnut charts */}
          <div className="accounting-charts-grid">
            {expenseDoughnut && (
              <div className="card">
                <p className="card-title">Gider Dağılımı (Kategori)</p>
                <div className="doughnut-container">
                  <Doughnut data={expenseDoughnut} options={doughnutOptions} />
                </div>
              </div>
            )}
            {incomeDoughnut && (
              <div className="card">
                <p className="card-title">Gelir Dağılımı (Kaynak)</p>
                <div className="doughnut-container">
                  <Doughnut data={incomeDoughnut} options={doughnutOptions} />
                </div>
              </div>
            )}
            {paymentDoughnut && (
              <div className="card">
                <p className="card-title">Ödeme Yöntemi Dağılımı</p>
                <div className="doughnut-container">
                  <Doughnut data={paymentDoughnut} options={doughnutOptions} />
                </div>
              </div>
            )}
          </div>

          {/* Detailed breakdown tables */}
          {summary.expensesByCategory.length > 0 && (
            <div className="card">
              <p className="card-title">Kategoriye Göre Giderler</p>
              <div className="table-wrapper">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Kategori</th>
                      <th style={{ textAlign: 'right' as const }}>Toplam</th>
                      <th style={{ textAlign: 'right' as const }}>Oran</th>
                    </tr>
                  </thead>
                  <tbody>
                    {summary.expensesByCategory.map((c) => (
                      <tr key={c.category}>
                        <td>{c.category}</td>
                        <td style={{ textAlign: 'right', fontWeight: 600 }}>{currency(c.total)}</td>
                        <td style={{ textAlign: 'right', color: '#64748b' }}>
                          {summary.totalExpenses > 0
                            ? `%${((c.total / summary.totalExpenses) * 100).toFixed(1)}`
                            : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {summary.incomeBySource.length > 0 && (
            <div className="card">
              <p className="card-title">Kaynağa Göre Gelirler</p>
              <div className="table-wrapper">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Kaynak</th>
                      <th style={{ textAlign: 'right' as const }}>Toplam</th>
                      <th style={{ textAlign: 'right' as const }}>Oran</th>
                    </tr>
                  </thead>
                  <tbody>
                    {summary.incomeBySource.map((s) => (
                      <tr key={s.source}>
                        <td>{s.source}</td>
                        <td style={{ textAlign: 'right', fontWeight: 600 }}>{currency(s.total)}</td>
                        <td style={{ textAlign: 'right', color: '#64748b' }}>
                          {summary.totalIncome > 0
                            ? `%${((s.total / summary.totalIncome) * 100).toFixed(1)}`
                            : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      ) : null}
    </>
  );
}
